/**
 * Copyright 2023 Phenix Real Time Solutions, Inc. All Rights Reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed onmediaSourceUri an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import {spawn, ChildProcess} from 'child_process';

export default class RtmpPush {
  private _mediaSourceUri: string;
  private _rtmpPushUri: string;
  private _isffmpegProcessKilled?: boolean;
  private _ffmpegProcess?: ChildProcess;

  constructor(mediaSourceUri: string, rtmpPushUri: string) {
    this._mediaSourceUri = mediaSourceUri;
    this._rtmpPushUri = rtmpPushUri;
    this._isffmpegProcessKilled = false;
  }

  async start(): Promise<RtmpPush> {
    try {
      if (!this._mediaSourceUri) {
        throw new Error('mediaSourceUri was not provided.');
      }

      if (!this._rtmpPushUri) {
        throw new Error('rtmpPushUri was not provided.');
      }

      if (!this._isValidUri(this._mediaSourceUri)) {
        throw new Error(`Provided mediaSourceUri [${this._mediaSourceUri}] is not a valid uri.`);
      }

      if (!this._isValidRtmpUri(this._rtmpPushUri)) {
        throw new Error('Invalid RTMP Push Uri');
      }

      let ffmpegParams = `-hide_banner -loglevel error -re -i ${this._mediaSourceUri} -c:a copy -c:v copy -f flv ${this._rtmpPushUri}`;

      if (this._mediaSourceUri.match('.txt')) {
        ffmpegParams = `-hide_banner -loglevel error -re -f concat -i ${this._mediaSourceUri} -c:a copy -c:v copy -f flv ${this._rtmpPushUri}`;
      }

      const paramsAsArray = ffmpegParams.split(' ');
      console.log(`[${new Date().toISOString()}] [RTMP Push] Starting ffmpeg with params [${paramsAsArray}]`);
      this._ffmpegProcess = spawn('ffmpeg', paramsAsArray);

      this._ffmpegProcess?.stdout?.on('data', (data: string) => {
        console.log(`[${new Date().toISOString()}] [RTMP Push] [ffmpeg] process successfully started with stdout [${data}]`);
      });

      this._ffmpegProcess?.stderr?.on('data', (data: string) => {
        throw new Error(`[${new Date().toISOString()}] [RTMP Push] [ffmpeg] process has failed with stderr [${data}]`);
      });

      this._ffmpegProcess?.on('error', (err: string) => {
        throw new Error(`[${new Date().toISOString()}] [RTMP Push] [ffmpeg] process [${this._ffmpegProcess?.pid}] could not start, error: ${err}`);
      });

      this._ffmpegProcess?.on('close', (code: number) => {
        this._isffmpegProcessKilled = true;
        console.log(`[${new Date().toISOString()}] [RTMP Push] [ffmpeg] process [${this._ffmpegProcess?.pid}] closed stdio with code [${code}]`);
      });

      this._ffmpegProcess?.on('exit', (code: number) => {
        console.log(`[${new Date().toISOString()}] [RTMP Push] [ffmpeg] process [${this._ffmpegProcess?.pid}] exited with code [${code}]`);
      });

      console.log(`[${new Date().toISOString()}] [RTMP Push] Stream is ready`);

      return this;
    } catch (error) {
      console.error(`[${new Date().toISOString()}] [RTMP Push] Start failed due to error - ${error}`);

      return this;
    }
  }

  async stop(): Promise<void> {
    try {
      if (!this._ffmpegProcess) {
        throw new Error('[RTMP Push] [ffmpeg] process was not found.');
      }

      if (this._isffmpegProcessKilled === true) {
        throw new Error(`[RTMP Push] [ffmpeg] process [${this._ffmpegProcess.pid}] already killed.`);
      }

      let isFfmpegKilled = await this._killProcessWithGivenSignal('SIGTERM');

      if (!isFfmpegKilled || !this._ffmpegProcess.killed) {
        isFfmpegKilled = await this._killProcessWithGivenSignal('SIGKILL');

        if (!isFfmpegKilled || !this._ffmpegProcess.killed) {
          throw new Error(`[RTMP Push] [ffmpeg] process with processId [${this._ffmpegProcess.pid}] was not terminated.`);
        }
      }
    } catch (error) {
      console.error(`[${new Date().toISOString()}] [RTMP Push] Stop failed due to error - ${error}`);
    }
  }

  private _isValidUri(uriString: string): boolean {
    try {
      return Boolean(new URL(uriString));
    } catch (e) {
      return false;
    }
  }

  private _isValidRtmpUri(uri: string): boolean {
    const validRtmpRegex = new RegExp(/^(rtmp:\/\/ingest)|(?:phenixrts.com:80\/ingest)|(?:capabilities=)/);

    return validRtmpRegex.test(uri);
  }

  private async _killProcessWithGivenSignal(killSignal: NodeJS.Signals) {
    let timeoutHandle: NodeJS.Timeout;
    const timeoutDuration = 3000;

    console.log(`[${new Date().toISOString()}] [RTMP Push] Killing [ffmpeg] process with processId [${this._ffmpegProcess?.pid}] using signal [${killSignal}]`);

    const killTimeout = new Promise(resolve => {
      timeoutHandle = setTimeout(() => {
        console.log(
          `[${new Date().toISOString()}] [RTMP Push] Killing timeout [${timeoutDuration}]ms was reached when attempting to kill [ffmpeg] process [${this._ffmpegProcess?.pid}] with signal [${killSignal}]`
        );
        resolve(false);
      }, timeoutDuration);
    });
    const killProcess = new Promise(resolve => {
      this._ffmpegProcess?.kill(killSignal);

      this._ffmpegProcess?.on('close', () => {
        resolve(true);
      });

      this._ffmpegProcess?.on('error', () => {
        resolve(false);
      });
    });

    return Promise.race([killTimeout, killProcess])
      .then(result => {
        if (result) {
          clearTimeout(timeoutHandle);
        }

        return result;
      });
  }
}